import { ItemStatus, TeamType } from "@/models"
import { Filter } from "@/models/Pageable"

import { ItemSeverityLevel, ItemType } from "../Item"
import { FilterBacklogUI } from "./FilterBacklogUI"

import type { StoryStatusGroup } from "../Item"

export type ItemQueryPriorityType =
  | "BACKLOG"
  | "SPRINT"
  | "EPIC"
  | "UNPRIORITIZED"
  | "MY_WORK"
  | "SINGLE_LIST"
  | ""
export type SortPriority =
  | "backlogPriority"
  | "sprintPriority"
  | "epicPriority"
  | "roadMapPriority"
  | "title"
  | "unprioritizedOrder"

export interface ItemQueryFilter {
  text?: string
  q?: string
  itemTypes?: ItemType[]
  storyStatuses?: ItemStatus[]
  storyStatusGroup?: StoryStatusGroup[]
  ownerIds?: string[]
  taskOwners?: string[]
  hasStoryPoints?: boolean
  hasEstimation?: boolean
  hasBacklogPriority?: boolean
  sprintIds?: string[]
  inSprint?: boolean
  epicIds?: string[]
  inEpic?: boolean
  teamId?: string
  startCreatedDateTime?: string
  endCreatedDateTime?: string
  sortKey?: SortPriority[]
  size?: number
  hasActiveStoryPoker?: boolean
  excludeStoryStatuses?: string[]
  includeTeamLessSupportCases?: boolean
  severityLevel?: ItemSeverityLevel[]
  sequenceNumberPrefix?: string
  hasEstimationOrStoryPoints?: boolean
  teamIds?: string[]
  inActiveSprint?: boolean
  inNotActiveSprint?: boolean
  completed?: boolean
  withLatestStatusChangeComment?: boolean
  hasTeam?: boolean
  itemStatuses?: ItemStatus[]
  archived?: boolean
  skipTeam?: boolean
  projectIds?: string[]
  milestoneIds?: string[]
  hasMilestone?: boolean
  currentProjectId?: string
  skipPermissionCheck?: boolean
  teamType?: TeamType
  singleListView?: boolean
  all?: boolean
}

export class FilterBacklog extends Filter {
  text = ""
  q = ""
  itemTypes: ItemType[] = []
  storyStatuses: ItemStatus[] = []
  ownerIds: string[] = []
  taskOwners: string[] = []
  withLatestStatusChangeComment = false
  hasStoryPoints?: boolean = undefined
  hasEstimation?: boolean = undefined
  hasBacklogPriority?: boolean = undefined
  sprintIds: string[] = []
  inSprint?: boolean = undefined
  epicIds: string[] = []
  inEpic?: boolean = undefined
  teamId?: string = undefined
  teamIds: string[] = []
  startCreatedDateTime = ""
  endCreatedDateTime = ""
  inMilestone?: boolean = undefined
  archived?: boolean = undefined
  sortKey: SortPriority[] = ["backlogPriority"]
  label?: string
  status?: string
  tShirtSize?: string
  hasActiveStoryPoker?: boolean
  excludeStoryStatuses?: ItemStatus[]
  hasTShirt: boolean
  includeTeamLessSupportCases?: boolean
  severityLevel?: ItemSeverityLevel[]
  sequenceNumberPrefix?: string
  hasEstimationOrStoryPoints?: boolean
  inActiveSprint?: boolean
  inNotActiveSprint?: boolean
  completed?: boolean
  itemIds?: string[] = []
  onlyGet?: boolean
  withoutPagination = false
  hasTeam?: boolean = undefined
  projectIds?: string[] = []
  milestoneIds?: string[] = []
  hasMilestone?: boolean = undefined
  projectId?: string
  currentProjectId?: string
  skipPermissionCheck?: boolean
  teamType?: TeamType
  singleListView?: boolean
  skipTeam?: boolean
  all?: boolean
  storyStatusGroup: StoryStatusGroup[] = []

  constructor(props) {
    super(props)

    this.text = props.text
    this.q = props.q
    this.itemTypes = props.itemTypes
    this.storyStatuses = props.storyStatuses
    this.ownerIds = props.ownerIds
    this.taskOwners = props.taskOwners
    this.hasStoryPoints = props.hasStoryPoints
    this.hasEstimation = props.hasEstimation
    this.hasBacklogPriority = props.hasBacklogPriority
    this.sprintIds = props.sprintIds
    this.inSprint = props.inSprint
    this.epicIds = props.epicIds
    this.inEpic = props.inEpic
    this.teamId = props.teamId
    this.teamIds = props.teamIds
    this.startCreatedDateTime = props.startCreatedDateTime
    this.endCreatedDateTime = props.endCreatedDateTime
    this.sortKey = props.sortKey || this.sortKey
    this.archived = props.archived
    this.label = props.label
    this.status = props.status
    this.tShirtSize = props.tShirtSize
    this.hasActiveStoryPoker = props.hasActiveStoryPoker
    this.excludeStoryStatuses = props.excludeStoryStatuses
    const sortKeys = this.getSortKeys()
    this.addMultipleSort(sortKeys.map(field => ({ field, isAsc: true })))
    this.hasTShirt = props.hasTShirt
    this.includeTeamLessSupportCases = props.includeTeamLessSupportCases
    this.severityLevel = props.severityLevel
    this.sequenceNumberPrefix = props.sequenceNumberPrefix
    this.hasEstimationOrStoryPoints = props.hasEstimationOrStoryPoints
    this.inActiveSprint = props.inActiveSprint
    this.inNotActiveSprint = props.inNotActiveSprint
    this.completed = props.completed
    this.withLatestStatusChangeComment = props.withLatestStatusChangeComment
    this.itemIds = props.itemIds
    this.onlyGet = props.onlyGet
    this.withoutPagination = props.withoutPagination
    this.hasTeam = props.hasTeam
    this.projectIds = props.projectIds
    this.milestoneIds = props.milestoneIds
    this.hasMilestone = props.hasMilestone
    this.projectId = props.projectId
    this.currentProjectId = props.currentProjectId
    this.skipPermissionCheck = props.skipPermissionCheck
    this.teamType = props.teamType
    this.singleListView = props.singleListView
    this.skipTeam = props.skipTeam
    this.all = props.all
    this.storyStatusGroup = props.storyStatusGroup ?? []
  }

  /**
   * Sends selected statuses and predefined status groups together; the backend
   * unions both fields into one search set.
   */
  getJsonObj() {
    const data = {
      text: this.text,
      q: this.q,
      itemTypes: this.itemTypes,
      ownerIds: this.ownerIds,
      taskOwners: this.taskOwners,
      hasStoryPoints: this.hasStoryPoints,
      hasEstimation: this.hasEstimation,
      hasBacklogPriority: this.hasBacklogPriority,
      sprintIds: this.sprintIds,
      inSprint: this.inSprint,
      epicIds: this.epicIds,
      inEpic: this.inEpic,
      teamId: this.skipTeam ? null : this.teamId,
      teamIds: this.skipTeam ? null : this.teamIds,
      startCreatedDateTime: this.startCreatedDateTime,
      endCreatedDateTime: this.endCreatedDateTime,
      size: this.size ?? this.defaultSize,
      inMilestone: this.inMilestone,
      archived: this.archived,
      label: this.label,
      status: this.status,
      tShirtSize: this.tShirtSize,
      hasActiveStoryPoker: this.hasActiveStoryPoker,
      excludeStoryStatuses: this.getExcludeStoryStatuses(),
      storyStatuses: this.storyStatuses,
      storyStatusGroup: this.storyStatusGroup,
      hasTShirt: this.hasTShirt ?? "",
      includeTeamLessSupportCases: this.includeTeamLessSupportCases ?? "",
      severityLevel: this.severityLevel ?? [],
      sequenceNumberPrefix: this.sequenceNumberPrefix ?? "",
      hasEstimationOrStoryPoints: this.hasEstimationOrStoryPoints,
      inActiveSprint: this.inActiveSprint,
      inNotActiveSprint: this.inNotActiveSprint,
      completed: this.completed,
      withLatestStatusChangeComment: this.withLatestStatusChangeComment,
      itemIds: this.itemIds,
      hasTeam: this.hasTeam,
      projectIds: this.projectIds,
      milestoneIds: this.milestoneIds,
      hasMilestone: this.hasMilestone,
      currentProjectId: this.currentProjectId,
      skipPermissionCheck: this.skipPermissionCheck,
      teamType: this.teamType,
      singleListView: this.singleListView,
      all: this.all
    }

    return super.getJsonObj({
      params: data,
      withoutPagination: this.withoutPagination
    })
  }

  resetPagination() {
    this.page = 0
    this.size = this.defaultSize
    this.prevSize = undefined
  }

  getExcludeStoryStatuses() {
    if (this.storyStatuses && this.storyStatuses[0] === "ALL") {
      this.storyStatuses = []
      return []
    }

    if (!this.excludeStoryStatuses?.length) return []
    return this.excludeStoryStatuses.filter(status => !this.storyStatuses?.includes(status))
  }

  setFilterNotEstimated(value?: boolean) {
    this.hasEstimation = value ? !value : undefined
  }

  setFilterEstimated(value?: boolean) {
    this.hasEstimation = value ? value : undefined
  }

  setStoryPointsEstimated(value?: boolean) {
    this.hasStoryPoints = value ? value : undefined
  }

  setFilterStatus(status?: ItemStatus) {
    this.storyStatuses = status ? [status] : []
  }

  setFilterNotInEpic() {
    this.inEpic = false
  }

  setArchivedEpic(archived) {
    this.archived = archived
  }

  setSize(size) {
    this.size = size
  }

  setEpicLabel(label) {
    this.label = label
  }

  setEpicStatus(status) {
    this.status = status
  }

  setItemTypes(itemTypes: ItemType[]) {
    this.itemTypes = itemTypes
  }

  setTShortSize(size) {
    this.tShirtSize = size
  }

  setHasTShirt(hasTShirt: boolean) {
    this.hasTShirt = hasTShirt
  }

  setSearchText(text) {
    this.q = text
  }

  setFilterTeam(teamId?: string) {
    this.teamId = teamId ? teamId : undefined
  }

  setInMilestone(inMilestone) {
    this.inMilestone = inMilestone
  }

  setFilterTeams(teamIds?: string[]) {
    this.teamIds = this.checkSupportCaseFilter ? [] : (teamIds ?? [])
  }

  setFilter(filter) {
    Object.keys(filter).forEach(field => {
      this[field] = filter[field]
    })
  }

  getSortKeys(): SortPriority[] {
    return this.sortKey.length ? this.sortKey : ["backlogPriority"]
  }

  updateFilterByField({ id, value }) {
    this[id] = value
  }

  createCurrentPositionFilter() {
    const filter = Object.assign(FilterBacklog.create({}), this)

    if (filter.withoutPagination) {
      filter.all = true
      return filter
    }

    filter.page = 1
    filter.size = (this.page + 1) * this.size
    filter.all = undefined

    return filter
  }

  updateFilterSizeByElements(numberOfElements: number) {
    this.size = this.defaultSize
    this.prevSize =
      numberOfElements > 0 ? numberOfElements - (this.page - 1) * this.defaultSize : undefined

    if (numberOfElements <= 0) return

    this.page = Math.min(this.page, numberOfElements)
  }

  get hasSelectedFilters() {
    return !!this.selectedFiltersCount
  }

  get checkSupportCaseFilter(): boolean {
    return !!this.itemTypes?.includes("SUPPORT_CASE")
  }

  get selectedFiltersCount() {
    let count = 0
    let changedFilterUI = [
      "text",
      "storyStatuses",
      "storyStatusGroup",
      "ownerIds",
      "epicIds",
      "startCreatedDateTime",
      "hasEstimation",
      "hasStoryPoints",
      "severityLevel",
      "sequenceNumberPrefix"
    ]

    if (this.getSortKeys().includes("epicPriority")) {
      changedFilterUI = changedFilterUI.filter(e => e !== "epicIds")
      changedFilterUI.push("teamIds")
    }

    changedFilterUI.forEach(filterKey => {
      if (this.hasSelectedFilterByKey(filterKey)) {
        count++
      }
    })

    // check item types in filter only for sprint backlogs
    if (this.sprintIds?.length && this.hasSelectedFilterByKey("itemTypes")) {
      count++
    }

    return count
  }

  hasSelectedFilterByKey(key) {
    if (!key || !this[key]) return false
    if (typeof this[key] === "boolean") return this[key]

    return this[key].length > 0
  }

  static create(props) {
    return new FilterBacklog(props)
  }

  static createFilterFromUI(filter, props) {
    const newFilter = new FilterBacklog(filter)
    const payload = FilterBacklogUI.create(props).getJsonObj()

    Object.keys(payload).forEach(key => (newFilter[key] = payload[key]))
    return newFilter
  }
}

export class FilterBacklogAnalytics extends FilterBacklog {}
